import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Inbox, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LOGO_URL } from "../lib/constants";
import { KycDocuments } from "../components/KycDocuments";

const KIND_LABEL = {
  contact: "General Enquiry",
  private_access: "Private Access",
  asset_inquiry: "Asset Enquiry",
  buyer_search: "Bespoke Search",
  seller_listing: "Seller — Expertise",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!user) return;
    const endpoint = isAdmin ? "/requests" : "/my-requests";
    api
      .get(endpoint)
      .then(({ data }) => setRequests(data))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [user, isAdmin]);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-navy grain">
      <header className="glass sticky top-0 z-40 px-6 lg:px-10 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Royce International" className="h-11 w-auto object-contain" />
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 border border-white/20 text-white/80 px-5 py-2.5 rounded-md text-[11px] tracking-wide-lux uppercase hover:border-gold hover:text-gold transition-all"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14 md:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-sans text-gold text-[11px] tracking-luxury uppercase mb-4 flex items-center gap-2">
            {isAdmin ? <ShieldCheck className="w-4 h-4" /> : <Inbox className="w-4 h-4" />}
            {isAdmin ? "Administrator" : "Private Client"}
          </p>
          <h1 data-testid="dashboard-title" className="font-serif text-white text-4xl md:text-6xl mb-3">
            Welcome, <span className="italic text-gold">{user?.name}</span>
          </h1>
          <p className="font-sans text-white/50 text-sm md:text-base max-w-xl">
            {isAdmin
              ? "All private enquiries submitted across the platform are listed below."
              : "Your confidential enquiries and requests are listed below."}
          </p>
        </motion.div>

        <div className="mt-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-white text-2xl">{isAdmin ? "All Requests" : "My Requests"}</h2>
            <span data-testid="requests-count" className="font-sans text-gold text-sm tracking-wide-lux">
              {requests.length} total
            </span>
          </div>

          {loading ? (
            <p className="font-sans text-white/40 text-sm">Loading…</p>
          ) : requests.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center" data-testid="empty-state">
              <p className="font-serif text-white text-2xl mb-2">No requests yet</p>
              <p className="font-sans text-white/40 text-sm mb-6">
                {isAdmin ? "Enquiries will appear here as clients submit them." : "Submit an enquiry from the collection to get started."}
              </p>
              <Link to="/" className="inline-block bg-gold text-navy px-8 py-3 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold-light transition-colors">
                Explore Collection
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((r, i) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  data-testid={`request-item-${i}`}
                  className="glass rounded-xl p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-8"
                >
                  <div className="md:w-40 shrink-0">
                    <span className="inline-block bg-gold/15 text-gold text-[10px] tracking-wide-lux uppercase px-3 py-1.5 rounded">
                      {KIND_LABEL[r.kind] || r.kind}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-white text-xl">
                      {r.asset_type || r.search_type || "General enquiry"}
                    </p>
                    {isAdmin && (
                      <p className="font-sans text-white/75 text-sm mt-1">
                        {r.name} · {r.email} {r.phone ? `· ${r.phone}` : ""}
                      </p>
                    )}
                    {r.message && <p className="font-sans text-white/60 text-sm mt-2 line-clamp-2">{r.message}</p>}
                  </div>
                  <div className="md:text-right shrink-0">
                    {r.budget && <p className="font-sans text-gold text-sm">{r.budget}</p>}
                    <p className="font-sans text-white/50 text-xs mt-1">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {!isAdmin && <KycDocuments />}
      </main>
    </div>
  );
}
